import cx from 'classnames';
import { UserRole } from 'models/enum';
import { ChartActionsComponentProps, ChartMetaFormField, ContentItem } from 'models/ui';
import { Box, ChartPermission, ErrorCard, ErrorFormField, SectionDivider } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem } from 'components/common/els';
import { ChartViewProps } from 'components/features/shared/constants';
import {
  DISABILITY_OTHER_VALUE,
  FormField,
  FormFieldLabel,
  PLACEHOLDER_OPTION_ID,
  Placeholder,
  REQUIRED_FORM_FIELDS,
  STATIC_LEGAL_TEXT,
  SectionTitle,
  TEXT_AREA_MAX_LENGTH,
  buildClearItem,
  buildPlaceholderItem
} from './constants';
import DisabilityAccommodationsDropdown from './DisabilityAccommodationsDropdown';
import InformedConsentDropdown from './InformedConsentDropdown';
import { InformedConsentChartHistory } from './InformedConsentHistory';
import './informed.consent.scss';

interface InformedConsentViewProps extends ChartViewProps {
  chartActionsProps: ChartActionsComponentProps;
  chartHistory: InformedConsentChartHistory[];
  deleteHistory: Function;
  isAuthor?: boolean;
  userRole?: string;
}

const isSyntheticDefaultItem = (item: ContentItem): boolean => item.id === '' && item.value === '';

const withDropdownPlaceholder = (formField: ChartMetaFormField | undefined, placeholder: string): ChartMetaFormField | undefined => {
  if (!formField) return formField;
  const existing = formField.chartContent ?? [];
  if (existing.some((item) => item.id === PLACEHOLDER_OPTION_ID)) {
    return formField;
  }
  const realOptions = existing.filter((item) => !isSyntheticDefaultItem(item));
  return { ...formField, chartContent: [buildPlaceholderItem(placeholder), buildClearItem(), ...realOptions] };
};

const asCustomDropdownField = (formField: ChartMetaFormField | undefined): ChartMetaFormField | undefined => (formField ? { ...formField, inputType: undefined } : formField);

const dropdownWrapperClass = (formField: ChartMetaFormField | undefined): string | undefined => {
  const value = String(formField?.value ?? '').trim();
  return value ? undefined : 'informed-consent__dropdown--placeholder';
};

const statementLine = (index: number, text: React.ReactNode, modifier?: string) => (
  <div className={cx('informed-consent__statement', modifier)}>
    <span className="informed-consent__item-number">{`${index}.`}</span>
    <span className="informed-consent__statement-text">{text}</span>
  </div>
);

const numberedItem = (index: number, statementText: React.ReactNode, belowNode?: React.ReactNode) => (
  <div className="informed-consent__item" key={`item-${index}`}>
    {statementLine(index, statementText)}
    {belowNode && <div className="informed-consent__field">{belowNode}</div>}
  </div>
);

const InformedConsentView = (props: InformedConsentViewProps) => {
  const { formFieldMap, formSubmittedCount, isAuthor, userRole, chartActionsProps } = props;
  const { enableSaveButton, enableDisplayRecordsButton, onSaveClick, onCancelClick, onDisplayRecordsClick } = chartActionsProps;

  const isFacultyViewer = !isAuthor && userRole === UserRole.INSTRUCTOR;

  const renderChartActions = ({ allowSaveFragment }: { allowSaveFragment?: boolean }) => {
    if (!allowSaveFragment || isFacultyViewer) return <></>;
    return (
      <div className="informed-consent__actions">
        <ELSFlex className="sng-form-buttons" left wrap>
          {isAuthor ? (
            <>
              <ELSFlexItem>
                <ELSButton type="primary" text="Save Records" isDisabled={!enableSaveButton} onClick={onSaveClick} />
              </ELSFlexItem>
              <ELSFlexItem>
                <ELSButton type="secondary" text="Return with data" isDisabled={!enableDisplayRecordsButton} onClick={onDisplayRecordsClick} />
              </ELSFlexItem>
            </>
          ) : (
            <>
              <ELSFlexItem>
                <ELSButton type="primary" text="Save & Complete" iconName="chevron-right" iconPosition="right" iconSize="1x" isDisabled={!enableSaveButton} onClick={onSaveClick} />
              </ELSFlexItem>
              <ELSFlexItem>
                <ELSButton type="secondary" text="Discard Changes" onClick={onCancelClick} />
              </ELSFlexItem>
            </>
          )}
        </ELSFlex>
      </div>
    );
  };

  const fieldOf = (field: FormField): ChartMetaFormField | undefined => {
    const formField = formFieldMap.get(field);
    return formField && REQUIRED_FORM_FIELDS.has(field) ? { ...formField, isRequired: true } : formField;
  };
  const labelless = (field: FormField): ChartMetaFormField | undefined => {
    const f = fieldOf(field);
    return f ? { ...f, label: '' } : f;
  };

  // Labeled inputs (signatures, risk boxes, interpreter fields keep their own labels).
  const textField = (field: FormField, placeholder: string) => <ErrorFormField formField={fieldOf(field)} formSubmittedCount={formSubmittedCount} inputProps={{ placeholder }} />;
  const dropdownField = (field: FormField, placeholder: string) => (
    <div className={dropdownWrapperClass(fieldOf(field))}>
      <ErrorFormField formField={asCustomDropdownField(withDropdownPlaceholder(fieldOf(field), placeholder))} formSubmittedCount={formSubmittedCount}>
        <InformedConsentDropdown chartFormField={withDropdownPlaceholder(fieldOf(field), placeholder)} placeholder={placeholder} />
      </ErrorFormField>
    </div>
  );

  // Label-less inputs: the numbered statement is rendered separately, so the input sits directly below it.
  // hideAsterisk because the point is optional and the visible statement carries the meaning (no stray asterisk on the empty label).
  const textInput = (field: FormField, placeholder: string, inputProps: Record<string, unknown> = {}) => (
    <ErrorFormField formField={labelless(field)} formSubmittedCount={formSubmittedCount} inputProps={{ placeholder, ...inputProps }} useDefaultMargin={false} hideAsterisk />
  );

  const characterCount = (field: FormField) => {
    const currentLength = String(fieldOf(field)?.value ?? '').length;
    return <div className="informed-consent__char-count">{`${currentLength}/${TEXT_AREA_MAX_LENGTH} characters`}</div>;
  };

  const textAreaInput = (field: FormField, placeholder: string) => (
    <div className="informed-consent__text-area">
      {textInput(field, placeholder, { maxLength: TEXT_AREA_MAX_LENGTH })}
      {characterCount(field)}
    </div>
  );

  const textAreaField = (field: FormField, placeholder: string) => (
    <div className="informed-consent__text-area">
      <ErrorFormField formField={fieldOf(field)} formSubmittedCount={formSubmittedCount} inputProps={{ placeholder, maxLength: TEXT_AREA_MAX_LENGTH }} useDefaultMargin={false} />
      {characterCount(field)}
    </div>
  );

  const isDisabilityOtherSelected = () =>
    Boolean(fieldOf(FormField.DISABILITY_ACCOMMODATIONS)?.chartContent?.some((item) => item.selected && item.value === DISABILITY_OTHER_VALUE));

  const renderDisabilityAccommodations = () => {
    const disabilityField = fieldOf(FormField.DISABILITY_ACCOMMODATIONS);
    if (!disabilityField) return null;
    return <DisabilityAccommodationsDropdown chartFormField={disabilityField} placeholder={Placeholder.DISABILITY} ariaLabel={FormFieldLabel.DISABILITY_ACCOMMODATIONS} />;
  };

  const dropdownInput = (field: FormField, placeholder: string) => (
    <div className={dropdownWrapperClass(fieldOf(field))}>
      <ErrorFormField
        formField={asCustomDropdownField(withDropdownPlaceholder(labelless(field), placeholder))}
        formSubmittedCount={formSubmittedCount}
        useDefaultMargin={false}
        hideAsterisk
      >
        <InformedConsentDropdown chartFormField={withDropdownPlaceholder(labelless(field), placeholder)} placeholder={placeholder} />
      </ErrorFormField>
    </div>
  );

  // Risk box with its a./b./c./d. label rendered in line with the input box.
  const riskField = (field: FormField, label: string) => (
    <div className="informed-consent__risk">
      <span className="informed-consent__risk-label">{label}</span>
      <div className="informed-consent__risk-input">{textInput(field, Placeholder.MATERIAL_RISK)}</div>
    </div>
  );

  return (
    <div className="informed-consent">
      <h1 className="informed-consent__title">{SectionTitle.INFORMED_CONSENT}</h1>
      <h3 className="informed-consent__subtitle">{STATIC_LEGAL_TEXT.PROCEDURE_SUBTITLE}</h3>
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />


      <SectionDivider />

      {numberedItem(1, FormFieldLabel.PROCEDURE_DESCRIPTION, textAreaInput(FormField.PROCEDURE_DESCRIPTION, Placeholder.PROCEDURE_DESCRIPTION))}
      {numberedItem(2, FormFieldLabel.CONDITION_DIAGNOSIS, textAreaInput(FormField.CONDITION_DIAGNOSIS, Placeholder.CONDITION_DIAGNOSIS))}
      {numberedItem(3, FormFieldLabel.PURPOSE, textAreaInput(FormField.PURPOSE, Placeholder.PURPOSE))}

      {numberedItem(
        4,
        STATIC_LEGAL_TEXT.ITEM_4_INTRO,
        <ELSFlex left wrap className="u-flex-gutter-2x u-flex-row-gap-1x informed-consent__risks">
          <ELSFlexItem w="1o2" md="1o1">
            {riskField(FormField.MATERIAL_RISK_A, FormFieldLabel.MATERIAL_RISK_A)}
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            {riskField(FormField.MATERIAL_RISK_B, FormFieldLabel.MATERIAL_RISK_B)}
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            {riskField(FormField.MATERIAL_RISK_C, FormFieldLabel.MATERIAL_RISK_C)}
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            {riskField(FormField.MATERIAL_RISK_D, FormFieldLabel.MATERIAL_RISK_D)}
          </ELSFlexItem>
        </ELSFlex>
      )}

      {numberedItem(5, STATIC_LEGAL_TEXT.ITEM_5)}

      <div className="informed-consent__item" key="item-6">
        {statementLine(
          6,
          <>
            <span>{FormFieldLabel.LIKELIHOOD_OF_SUCCESS}</span>
            <span className="informed-consent__inline-field">{dropdownInput(FormField.LIKELIHOOD_OF_SUCCESS, Placeholder.LIKELIHOOD_OF_SUCCESS)}</span>
          </>,
          'informed-consent__statement--inline'
        )}
      </div>

      {numberedItem(7, FormFieldLabel.PROGNOSIS_IF_DECLINED, textAreaInput(FormField.PROGNOSIS_IF_DECLINED, Placeholder.PROGNOSIS_IF_DECLINED))}
      {numberedItem(8, FormFieldLabel.PRACTICAL_ALTERNATIVES, textAreaInput(FormField.PRACTICAL_ALTERNATIVES, Placeholder.PRACTICAL_ALTERNATIVES))}

      {numberedItem(9, STATIC_LEGAL_TEXT.ITEM_9)}
      {numberedItem(10, STATIC_LEGAL_TEXT.ITEM_10)}
      {numberedItem(11, STATIC_LEGAL_TEXT.ITEM_11)}
      {numberedItem(12, STATIC_LEGAL_TEXT.ITEM_12)}

      {numberedItem(
        13,
        STATIC_LEGAL_TEXT.ITEM_13_INTRO,
        <>
          <div className="informed-consent__sub">
            <span className="informed-consent__sub-label">a.</span>
            <span>{STATIC_LEGAL_TEXT.ITEM_13_BULLET_1}</span>
          </div>
          <div className="informed-consent__sub">
            <span className="informed-consent__sub-label">b.</span>
            <span>{STATIC_LEGAL_TEXT.ITEM_13_BULLET_2}</span>
          </div>
        </>
      )}

      {numberedItem(14, STATIC_LEGAL_TEXT.ITEM_14)}
      {numberedItem(15, STATIC_LEGAL_TEXT.ITEM_15)}
      {numberedItem(16, STATIC_LEGAL_TEXT.ITEM_16)}

      <div className="informed-consent__item" key="item-17">
        {statementLine(17, STATIC_LEGAL_TEXT.INTERPRETER_SECTION_TITLE)}
        <div className="informed-consent__field informed-consent__field--flush informed-consent__field--indented">
          <p className="informed-consent__legal-text">{STATIC_LEGAL_TEXT.INTERPRETER_MATERIALS_LINE}</p>
          <ELSFlex left wrap className="u-flex-gutter-3x u-flex-row-gap-1x">
            <ELSFlexItem w="1o2" md="1o1">
              {textField(FormField.INTERPRETER_NAMES, Placeholder.INTERPRETER_NAMES)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {dropdownField(FormField.INTERPRETER_MODALITY, Placeholder.INTERPRETER_MODALITY)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {dropdownField(FormField.LANGUAGE, Placeholder.LANGUAGE)}
            </ELSFlexItem>
          </ELSFlex>
        </div>
      </div>

      <div className="informed-consent__item informed-consent__item--last" key="item-18">
        {statementLine(18, STATIC_LEGAL_TEXT.DISABILITY_SECTION_TITLE)}
        <div className="informed-consent__field informed-consent__field--indented">
          {renderDisabilityAccommodations()}
          {isDisabilityOtherSelected() && (
            <div className="informed-consent__disability-other">
              <ErrorFormField formField={fieldOf(FormField.DISABILITY_OTHER)} formSubmittedCount={formSubmittedCount} useDefaultMargin={false} />
            </div>
          )}
        </div>
      </div>

      <SectionDivider />

      <Box className="informed-consent__signatures" mt3>
        <div className="informed-consent__sign-row">
          <div className="informed-consent__sign-name">{textField(FormField.PATIENT_NAME, Placeholder.ENTER_NAME)}</div>
          <div className="informed-consent__sign-name">{textField(FormField.PATIENT_SIGNATURE, Placeholder.ENTER_NAME)}</div>
        </div>
        <p className="informed-consent__consent-note">[This consent will be valid for 30 days unless revoked.]</p>

        <SectionDivider />

        <div className="informed-consent__sign-row">
          <div className="informed-consent__sign-name">{textField(FormField.WITNESS_SIGNATURE, Placeholder.ENTER_NAME)}</div>
        </div>

        <SectionDivider />

        <div className="informed-consent__sign-row">
          <div className="informed-consent__sign-name">{textField(FormField.PHYSICIAN_SIGNATURE, Placeholder.ENTER_NAME)}</div>
        </div>

        <SectionDivider />

        <div className="informed-consent__guardian">
          <span className="informed-consent__guardian-prefix">
            <span className="chart-input__label-asterisk">*</span>
            <span>I</span>
          </span>
          <span className="informed-consent__guardian-input">{textInput(FormField.GUARDIAN_NAME, Placeholder.ENTER_NAME)}</span>
          <span className="informed-consent__guardian-text">{STATIC_LEGAL_TEXT.GUARDIAN_HELPER}</span>
        </div>

        <Box className="informed-consent__additional">{textAreaField(FormField.ADDITIONAL_COMMENTS, Placeholder.ADDITIONAL_COMMENTS)}</Box>
      </Box>

      <ChartPermission>{renderChartActions}</ChartPermission>
    </div>
  );
};

InformedConsentView.displayName = 'InformedConsentView';
export default InformedConsentView;
